import { inject, Injectable } from '@angular/core';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { Blueprint } from 'src/app/presentations/services/blueprint-generator.service';
import { BlueprintService } from 'src/app/template-editor/services/blueprint.service';
import { Company } from 'src/app/api/services/company';
import { CapAlertDisplayData } from '../../types/cap-alerts-page.types';

/** The alert text a template shows before a real alert arrives. */
const PLACEHOLDER_HEADLINE = 'ALERT HEADLINE';
const PLACEHOLDER_DESCRIPTION =
  'Alert Description This space will be populated with the description.';
const PLACEHOLDER_INSTRUCTION = 'Alert Instructions';

/** A component takes the alert's text when its value holds one of these tokens. */
const ALERT_TOKEN =
  /%(?:ENCODED_)?(?:HEADLINE|DESCRIPTION|INSTRUCTION)%|%ENABLE_AUDIO%/;

/** Java's `URLEncoder.encode(text, "UTF-8")`, which the backend uses for the encoded tokens. */
function encodeLikeBackend(text: string): string {
  return encodeURIComponent(text)
    .replace(/%20/g, '+')
    .replace(
      /[!'()~]/g,
      (character) => '%' + character.charCodeAt(0).toString(16).toUpperCase(),
    );
}

/** Replaces the tokens the way the backend does when it builds an alert. */
function fillTokens(value: string): string {
  return (
    value
      .replace(/%HEADLINE%/g, PLACEHOLDER_HEADLINE)
      .replace(/%DESCRIPTION%/g, PLACEHOLDER_DESCRIPTION)
      .replace(/%INSTRUCTION%/g, PLACEHOLDER_INSTRUCTION)
      .replace(/%ENCODED_HEADLINE%/g, encodeLikeBackend(PLACEHOLDER_HEADLINE))
      .replace(
        /%ENCODED_DESCRIPTION%/g,
        encodeLikeBackend(PLACEHOLDER_DESCRIPTION),
      )
      .replace(
        /%ENCODED_INSTRUCTION%/g,
        encodeLikeBackend(PLACEHOLDER_INSTRUCTION),
      )
      // The preview stays silent.
      .replace(/%ENABLE_AUDIO%/g, 'false')
  );
}

/**
 * The rules behind what the page shows of a template: its blueprint, what the
 * company contributes (address and published brand logo) and the placeholder
 * alert text.
 */
@Injectable({ providedIn: 'root' })
export class CapAlertCustomizationService {
  private readonly blueprintService = inject(BlueprintService);
  private readonly featuresService = inject(FeaturesService);

  /** Read-only: the template editor's own blueprint state is left alone. */
  getBlueprint(productCode: string): Promise<Blueprint> {
    return this.blueprintService.getBlueprintCached(productCode, true);
  }

  /**
   * Whether the company has a brand logo to show: the plan has branding,
   * branding is not switched off, and a logo has been *published* (a draft
   * logo never reaches a display).
   */
  hasBrandLogo(company: Company): boolean {
    const settings = company.settings ?? {};
    const switchedOff =
      settings.brandingEnabled === 'false' ||
      settings.brandingEnabled === false;

    return (
      !!this.featuresService.isFeatureAvailable(
        FeaturesService.FEATURE_BRANDING,
      ) &&
      !switchedOff &&
      !!settings.brandingLogoFile
    );
  }

  /** The `displayData` message the editor preview sends. */
  toDisplayData(company: Company): CapAlertDisplayData {
    const settings = company.settings ?? {};

    return {
      displayAddress: {
        city: company.city,
        province: company.province,
        country: company.country,
        postalCode: company.postalCode,
      },
      companyBranding: this.hasBrandLogo(company)
        ? {
            logoFile: settings.brandingLogoFile as string,
            baseColor: settings.brandingBaseColor as string,
            accentColor: settings.brandingAccentColor as string,
          }
        : {},
    };
  }

  /**
   * The `attributeData` message for the preview: every component of the
   * blueprint that takes alert text, filled with the placeholder text.
   */
  toPreviewAttributeData(blueprint: Blueprint): string {
    const components = (blueprint.components ?? [])
      .map((component) => ({
        id: component.id,
        value: (component.attributes?.['value'] as { value?: unknown })?.value,
      }))
      .filter(
        (component): component is { id: string; value: string } =>
          typeof component.value === 'string' &&
          ALERT_TOKEN.test(component.value),
      )
      .map((component) => ({
        id: component.id,
        value: fillTokens(component.value),
      }));

    return JSON.stringify({ components });
  }
}
